import { useCallback, useEffect, useState } from 'react'
import { isNativePlatform } from '@/lib/platform/platformDetection'
// Both `getStatus` and `addListener` route through the platform wrapper
// so the dev harness can drive offline state mid-session on real device
// builds (the harness fires synthetic `networkStatusChange` events on
// mode flip; the real plugin's listener also stays subscribed so genuine
// device events still flow). In production the wrapper degenerates to
// direct Capacitor calls via DCE.
import {
  addListener as addNetworkListener,
  getStatus as getNetworkStatus,
} from '@/lib/platform/networkStatus'

export function useNativeOnlineStatus(): boolean {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine)
  const isNative = isNativePlatform()

  const handleOnline = useCallback(() => setIsOnline(true), [])
  const handleOffline = useCallback(() => setIsOnline(false), [])

  useEffect(() => {
    if (!isNative) {
      setIsOnline(navigator.onLine)

      window.addEventListener('online', handleOnline)
      window.addEventListener('offline', handleOffline)

      return () => {
        window.removeEventListener('online', handleOnline)
        window.removeEventListener('offline', handleOffline)
      }
    }

    let mounted = true
    let networkHandle: { remove: () => Promise<void> | void } | null = null

    getNetworkStatus()
      .then(status => {
        if (mounted) setIsOnline(status.connected)
      })
      .catch(() => {
        if (mounted) setIsOnline(navigator.onLine)
      })

    addNetworkListener('networkStatusChange', status => {
      if (mounted) setIsOnline(status.connected)
    })
      .then(handle => {
        if (mounted) {
          networkHandle = handle
        } else {
          handle.remove()
        }
      })
      .catch(() => {
        // Fallback to browser events if Capacitor listener fails
      })

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      mounted = false
      networkHandle?.remove()
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [isNative, handleOnline, handleOffline])

  return isOnline
}
